import { MaybeRef, Ref, computed, readonly, ref, unref } from "vue";
import {
  FormDefinition,
  FormElementBase,
  FormElementRuleType,
  FormElementType,
  FormResponse,
  FormResponseAnswer,
  FormResponseStatus,
  HeadingFormElement,
  RepeaterHostAnswerValue,
  ValidationType,
  Workflow,
} from "~/api";
import { FormDisplayMode } from "~/NewFormBuilder/types/FormDisplayMode";
import { nonQuestionElements } from "~/NewFormBuilder/types/NonQuestionElements";
import { validationDict } from "~/NewFormBuilder/utils/validationTypeOptions";
import { getEnvConfig } from "~/services/appConfig";
import { useLoginAccountsStore } from "~/stores/LoginAccountsStore";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { useIsInPdfMode } from "~/stores/pdfModeStore";
import { apiClient } from "~/utils/apiClient";
import { emptyGuid } from "~/utils/constants";
import { formatDate } from "~/utils/dateUtils";
import { formResponseStatusDisplayNames } from "~/utils/enumDisplayNames";
import { showError } from "~/utils/handleAPIError";
import {
  isHeadingFormElement,
  isRepeaterAnswerValue,
} from "~/utils/typeGuards";
import { processTemplateSyntaxSugar } from "./processTemplateSyntaxSugar";
import { getIsRuleApplicable } from "./rulesEngine";
import { FormResponseAnswerMap, UseAnswer } from "./types";
import { setAnswerTimeoutMap, useAnswerWrapper } from "./useAnswer";

/**
 * Counts words in text, handling both plain text and HTML content
 * @param text - The text to count words in
 * @param isRichText - Whether the text is rich text (HTML) that needs HTML tag removal
 * @returns Number of words
 */
function countWords(text: string, isRichText: boolean = false): number {
  if (!text || text.trim() === "") {
    return 0;
  }

  let processedText = text;

  if (isRichText) {
    // Remove HTML tags and decode HTML entities for rich text
    const noHtml = text.replaceAll(/<[^>]*>/g, "");
    processedText = noHtml.replaceAll(/&[a-zA-Z0-9#]+;/g, "");
  }

  // Match words including contractions and possessives
  const words = processedText.match(/\b\w+([-'\u2018\u2019]\w+)*\b/g);
  return words ? words.length : 0;
}

/**
 * Checks if an answer is valid for a given form component
 * @param component - The form component to validate against
 * @param answerValue - The answer value to validate
 * @returns true if the answer is valid, false otherwise
 */
export function isAnswerValid(
  component: FormElementBase,
  answerValue: any,
): boolean {
  // Empty answers are always considered valid (allows deletion)
  if (answerValue === undefined || answerValue === null || answerValue === "") {
    return true;
  }

  const stringValue = answerValue.toString();

  // Check word count validation for components that support it
  if (
    component.type === FormElementType.SIMPLE_TEXT ||
    component.type === FormElementType.RICH_TEXT
  ) {
    const maxWordCount = (component.properties as any).maxWordCount;
    if (maxWordCount) {
      const isRichText = component.type === FormElementType.RICH_TEXT;
      const currentWordCount = countWords(stringValue, isRichText);
      if (currentWordCount > maxWordCount) {
        return false;
      }
    }
  }

  // Check format validation for SimpleText components
  if (component.type === FormElementType.SIMPLE_TEXT) {
    const validationType = (component.properties as any)
      .validationType as ValidationType;
    if (validationType) {
      const validator = validationDict[validationType]?.validator;
      if (validator && !validator(stringValue)) {
        return false;
      }
    }
  }

  return true;
}

/**
 * Handles site-related template substitution
 */
function handleSiteTemplate(propertyName: string): string {
  switch (propertyName) {
    case "Url":
      return getEnvConfig().entraRedirect ?? "";
    default:
      return "";
  }
}

export interface UseFormResponseArgs {
  /** The form */
  formDefinition: FormDefinition;
  /** The current form response we are editing. If not provided, we will not make any API calls to submit answers. */
  formResponse?: FormResponse;
  /** The display mode of the form (view, edit, etc). Default is edit */
  displayMode?: FormDisplayMode;
  /** True to disable the form's rules engine and show all questions */
  disableRulesEngine?: MaybeRef<boolean>;
  /** The workflow of the form */
  workflow?: Workflow;
  /** Answers from all forms that are marked for sharing */
  sharedAnswers?: Record<string, string>;
  /** Optional callback to trigger workflow refresh after status changes */
  onWorkflowRefresh?: () => Promise<void>;
}

export interface FormPage {
  components: FormElementBase[];
}
export interface FormResponseState {
  /** Readonly array of answers to all the form's questions. Key is the question code of the element (question). */
  readonly answers: Readonly<FormResponseAnswerMap>;
  /** Substitute templates in the provided text using the templating engine's logic. Ex. substitute "{{QuestionCode}}" with the answer value. */
  readonly substituteTemplatedText: (
    text: string | undefined | null,
    repeatableGroupGuid?: string | null | undefined,
  ) => string | undefined | null;
  /** The provided form */
  readonly form: Readonly<FormDefinition>;
  /** The provided form response, if it exists */
  readonly formResponse?: Readonly<FormResponse>;
  /** Composable to get a writable ref to an answer. */
  useAnswer: UseAnswer;
  /** Gets if the provided element should be hidden based on conditional rules in this form */
  isHidden: (element: FormElementBase) => boolean;
  /** Gets if the provided element should be readonly based on conditional rules in this form */
  isReadOnly: (element: FormElementBase) => boolean;
  /** Returns true if the provided question code is available in the form (would be false if the question has restricted access)*/
  isAvailable: (questionCode: string) => boolean;
  /** Updates the status of the form response */
  updateFormResponseStatus: (status: FormResponseStatus) => void;
  /** Updates the status of the workflow */
  updateWorkflowStatus: (status: string) => void;
  /** Updates the name of the workflow in memory (used by SpecialQuestions) */
  updateWorkflowName: (name: string) => void;
  /** Pages of the form, based on the `Heading` elements with thier `pageBreakBefore` property set to true  */
  pages: Ref<FormPage[]>;
  /** Map of all currently in-fligh `submitAnswer` requests, and their timeout ids */
  inFlightRequestMap: typeof setAnswerTimeoutMap;
  /** Gathers all unanswered form components based on visibility, optionality, and answer state. It accounts for repeaters and nested (e.g. questions in compliance items) unanswered components. */
  getUnansweredQuestions: () => FormElementBase[];
  /** Gathers all questions requiring attention (unanswered OR with validation errors) for the Review Answers component. */
  getQuestionsRequiringAttention: () => FormElementBase[];
  /** Reloads the answers with the provided answer list. If an answer was deleted, the guid of the deleted answer can be provided to remove it from the answers map. */
  reloadAnswers: (
    answerList: FormResponseAnswer[],
    deletingGuid?: string,
    deletingRepeaterGuid?: string,
  ) => void;
  /** There is a local variable for nested unanswered questions. This function is called to update the local variable. The local variable is appended to the unanswered questions of the form when 'getUnansweredQuestions' is called. */
  updateNestedUnansweredQuestions: (
    answerList: FormElementBase[],
    forFormGuid?: string,
  ) => void;
  /** Fetches the recently updated answers and updates the answer map */
  refreshRecentlyUpdatedAnswers: () => void;
  /** Answers from all forms that are marked for sharing */
  readonly sharedAnswers?: Readonly<Record<string, string>>;
  /** The workflow associated with this form, if any */
  readonly workflow?: Readonly<Workflow>;
}

/**
 * Main composable for managing form responses, handling form state, rules, and answer submission
 *
 * This composable provides a comprehensive API for working with form responses, including:
 * - Managing form answers and their state
 * - Applying conditional rules (show/hide, read-only)
 * - Handling form pagination
 * - Submitting and updating answers
 * - Validating form completion
 *
 * @param {UseFormResponseArgs} options - Configuration options for the form response
 * @returns {FormResponseState} Form response state and utility functions
 */
export function useFormResponse({
  formDefinition,
  formResponse,
  displayMode,
  disableRulesEngine,
  workflow,
  sharedAnswers,
  onWorkflowRefresh,
}: UseFormResponseArgs): FormResponseState {
  // Default to edit mode if not specified
  displayMode ??= FormDisplayMode.EDIT;

  // Reactive PDF mode flag (drives isReadOnly carve-out for PDF output and is used by getUnanswered* etc.)
  const inPdf = useIsInPdfMode();

  // Build a map of form codes to their element guid maps for cross-form references
  const formCodeToElementGuidMap = new Map<string, Map<string, string>>();
  if (workflow?.formResponses) {
    for (const response of workflow.formResponses) {
      const formCode = response.formDefinition?.formCode;
      if (formCode && response.formDefinition?.components) {
        const elementMap = new Map(
          response.formDefinition.components.map((c) => [
            c.questionCode,
            c.guid,
          ]),
        );
        formCodeToElementGuidMap.set(formCode, elementMap);
      }
    }
  }

  // Initialize answers map from existing form response or empty array
  const answers = ref<FormResponseAnswerMap>(
    setAnswers(formDefinition, formResponse?.answers ?? []),
  );

  // Track questions from nested forms that are unanswered
  const nestedUnansweredQuestions = ref<FormElementBase[]>([]);

  /**
   * Computed property that identifies all heading elements that should create page breaks
   */
  const pageBreakElements = computed<HeadingFormElement[]>(() =>
    formDefinition.components
      .filter(isHeadingFormElement)
      .filter((c) => c.properties.addPageBreakBefore),
  );

  /**
   * Computed property that divides form components into pages based on page break elements
   * Each page contains components from the start index up to (but not including) the next page break
   */
  const pages = computed<FormPage[]>(() => {
    // Get indices of all page break elements
    const pageBreakIndices = pageBreakElements.value.map((c) =>
      formDefinition.components.indexOf(c),
    );
    const result: FormPage[] = [];

    // Create pages by slicing the components array at page break points
    for (let i = 0; i < pageBreakIndices.length + 1; i++) {
      const start = i === 0 ? 0 : pageBreakIndices[i - 1];
      const end = pageBreakIndices[i];

      const page: FormPage = {
        components: formDefinition.components.slice(start, end),
      };

      result.push(page);
    }

    return result;
  });

  /**
   * Creates a function that evaluates if a specific rule type applies to a form element
   *
   * @param {FormElementRuleType} ruleType - The type of rule to check (HIDDEN, READ_ONLY, etc.)
   * @returns {Function} A function that takes an element and returns whether the rule applies
   */
  function getIsRuleTypeApplicableWrapper(ruleType: FormElementRuleType) {
    return function (element: FormElementBase): boolean {
      // Don't apply any rules when the rules engine is disabled
      if (unref(disableRulesEngine)) {
        return false;
      }

      // Find all rules of the specified type that target this element or its parent
      const elementRules = formDefinition.rules.filter(
        (r) =>
          r.ruleType === ruleType &&
          (r.elementGuid === element.guid ||
            r.elementGuid === element.parentGuid),
      );

      // A rule is applicable if ANY of the rules evaluate to true
      const isApplicable = elementRules
        .map((rule) =>
          getIsRuleApplicable(
            rule,
            answers.value,
            formDefinition.components,
            formResponse,
            workflow,
          ),
        )
        .some(Boolean);

      // For hidden elements, update the answer's hidden state
      if (ruleType === FormElementRuleType.HIDDEN) {
        // If the element is hidden, we should not allow any changes to its answer
        const key = `${element.questionCode}-${element.repeaterGuid ?? emptyGuid}`;
        if (answers.value[key]) {
          answers.value[key].isHidden = isApplicable;
        }
      }

      return isApplicable;
    };
  }

  /**
   * Determines if a form element should be displayed as read-only
   *
   * An element is read-only if:
   * 1. It has the isReadOnly property set to true, or
   * 2. A READ_ONLY rule applies to it (not in PDF mode)
   *
   * Note: In READ_ONLY display mode outside of PDF mode, this returns true since all elements are read-only
   *
   * @param {FormElementBase} element - The form element to check
   * @returns {boolean} True if the element should be read-only
   */
  function isReadOnly(element: FormElementBase): boolean {
    if (displayMode === FormDisplayMode.READ_ONLY && !inPdf.value) {
      return true;
    }
    if (element.properties.isReadOnly) {
      return true;
    }
    return getIsRuleTypeApplicableWrapper(FormElementRuleType.READ_ONLY)(
      element,
    );
  }

  /**
   * Updates the status of the current form response
   *
   * Makes an API call to update the status and updates the local formResponse object
   *
   * @param {FormResponseStatus} status - The new status to set
   * @returns {Promise<void>}
   */
  async function updateFormResponseStatus(status: FormResponseStatus) {
    if (!formResponse?.guid) {
      showError("Form response guid is missing");
      return;
    }
    await apiClient.formResponseStatus
      .updateFormResponseStatus(formDefinition.guid, formResponse.guid, status)
      .then(() => {
        formResponse.status = status;
      })
      .catch(showError);
  }

  /**
   * Updates the status of the associated workflow
   *
   * Makes an API call to update the workflow status and updates the local workflow object
   *
   * @param {string} status - The new status code to set
   * @returns {Promise<void>}
   */
  async function updateWorkflowStatus(status: string) {
    if (!workflow) {
      showError("Workflow is missing");
      return;
    }
    workflow.status = status;
    await apiClient.workflowInstance
      .updateWorkflow(workflow.guid, workflow)
      .then(async () => {
        console.log("Workflow status updated to:", status);
        // Refresh workflow data to apply server-side filtering rules
        if (onWorkflowRefresh) {
          await onWorkflowRefresh();
        }
      })
      .catch(showError);
  }

  /**
   * Updates the workflow name in memory (used by SpecialQuestions to prevent stale data issues)
   *
   * @param {string} name - The new name to set
   */
  function updateWorkflowName(name: string) {
    if (!workflow) {
      showError("Workflow is missing");
      return;
    }
    console.log("Updating workflow name from", workflow.name, "to", name);
    workflow.name = name;
  }

  const myInfoStore = useMyInfoStore();
  const loginAccountsStore = useLoginAccountsStore();

  // Fire-and-forget: ensure account names are available for {{Form:CreatedBy}} substitution.
  // load() is internally cached and deduplicated, so this is safe to call eagerly.
  loginAccountsStore.load();

  /**
   * Handles login-related template substitution
   */
  function handleLoginTemplate(propertyName: string): string {
    switch (propertyName) {
      case "Name":
        return myInfoStore.userInfo?.loginName ?? "Unknown";
      case "Email":
        return myInfoStore.userInfo?.email ?? "Unknown";
      case "EntityName":
        return myInfoStore.currentSysOrg?.entityDto?.name ?? "Unknown";
      default:
        return "";
    }
  }

  /**
   * Handles workflow-related template substitution
   */
  function handleWorkflowTemplate(propertyName: string): string {
    if (!workflow) {
      return "";
    }

    switch (propertyName) {
      case "StatusCode":
        return workflow.status ?? "";
      case "StatusLabel": {
        const status = workflow.workflowDefinition?.statusList.find(
          (s) => s.code === workflow.status,
        );
        return status?.label ?? "";
      }
      case "EntityName":
        return workflow.entity?.name ?? "";
      default:
        return "";
    }
  }

  /**
   * Handles form-related template substitution
   */
  function handleFormTemplate(propertyName: string): string {
    if (!formResponse) {
      return "";
    }

    switch (propertyName) {
      case "StatusCode":
        return formResponse.status ?? "";
      case "StatusLabel": {
        return formResponseStatusDisplayNames[formResponse.status] ?? "";
      }
      case "CreatedBy":
        return loginAccountsStore.getName(formResponse.createdByUserGuid);
      case "CreatedAt":
        return formatDate(formResponse.createdAt);
      default:
        return "";
    }
  }

  /**
   * Handles organization-related template substitution
   */
  function handleOrgTemplate(propertyName: string): string {
    switch (propertyName) {
      case "Name":
        return myInfoStore.currentSysOrg?.name ?? "";
      case "Guid":
        return myInfoStore.currentSysOrg?.guid ?? "";
      default:
        return "";
    }
  }

  /**
   * Handles form code references (e.g., FormCode:QuestionCode)
   */
  function handleFormCodeTemplate(
    formCode: string,
    questionCodeFromOtherForm: string,
  ): string {
    const combinedKey = `${formCode}:${questionCodeFromOtherForm}`;

    if (!workflow?.formResponses) {
      return handleFallbackToSharedAnswers(combinedKey);
    }

    // Find the form response with the matching FormCode
    const targetFormResponse = workflow.formResponses.find(
      (fr) => fr.formDefinition?.formCode === formCode,
    );

    if (!targetFormResponse) {
      return handleFallbackToSharedAnswers(combinedKey);
    }

    const targetFormElementGuidMap = formCodeToElementGuidMap.get(formCode);
    if (!targetFormElementGuidMap) {
      return handleFallbackToSharedAnswers(combinedKey);
    }

    const thisAnswerFormElementGuid = targetFormElementGuidMap.get(
      questionCodeFromOtherForm,
    );

    if (!thisAnswerFormElementGuid) {
      return handleFallbackToSharedAnswers(combinedKey);
    }

    // Find the answer for the specified question code
    const targetAnswer = targetFormResponse.answers?.find((answer) => {
      return answer.formElementGuid === thisAnswerFormElementGuid;
    });

    if (!targetAnswer) {
      return handleFallbackToSharedAnswers(combinedKey);
    }

    try {
      // Parse the JSON answer value, consistent with existing code
      const parsedValue = JSON.parse(targetAnswer.answerValue);
      return parsedValue?.toString() ?? "";
    } catch {
      // If JSON parsing fails, use the raw value
      return targetAnswer.answerValue?.toString() ?? "";
    }
  }

  function handleFallbackToSharedAnswers(key: string): string {
    if (!sharedAnswers) {
      return "";
    }
    return sharedAnswers[key] ?? "";
  }

  /**
   * Handles special template syntax with colon (e.g., Login:Name, Workflow:StatusCode)
   */
  function handleSpecialTemplate(
    specialCode: string,
    propertyName: string,
  ): string {
    switch (specialCode) {
      case "Login":
        return handleLoginTemplate(propertyName);
      case "Workflow":
        return handleWorkflowTemplate(propertyName);
      case "Form":
        return handleFormTemplate(propertyName);
      case "Org":
        return handleOrgTemplate(propertyName);
      case "Site":
        return handleSiteTemplate(propertyName);
      default:
        // Handle form code references (e.g., FormCode:QuestionCode)
        return handleFormCodeTemplate(specialCode, propertyName);
    }
  }

  /**
   * Handles regular question code substitution
   */
  function handleRegularQuestionCode(
    questionCode: string,
    repeatableGroupGuid?: string | null,
  ): string | null {
    // Get all repeater components in this form
    const repeaterGuids = formDefinition.components
      .filter((c) => c.type === FormElementType.REPEATER)
      .map((r) => r.guid);

    // Find the component by question code
    const component = formDefinition.components.find(
      (c) => c.questionCode === questionCode,
    );

    // Determine if the component is inside a repeater
    const isInRepeater = component?.parentGuid
      ? repeaterGuids.includes(component.parentGuid)
      : false;

    // Determine which repeatable group GUID to use for the answer lookup:
    // - If component is inside a repeater, use the provided repeatableGroupGuid or default
    // - If component is not in a repeater, always use the default GUID
    const repeatableGroupGuidToUse = isInRepeater
      ? (repeatableGroupGuid ?? emptyGuid)
      : emptyGuid;

    // Look up the answer value using the component's question code and appropriate group GUID
    const key = questionCode + "-" + repeatableGroupGuidToUse;
    const answerValue = answers.value[key]?.answerValue;

    if (component?.type === FormElementType.REPEATER) {
      // Handle repeater-specific logic
      return (
        (
          answerValue as RepeaterHostAnswerValue
        )?.repeatableGroupGuids.length.toString() ?? null
      );
    }

    return answerValue?.toString() ?? null;
  }

  /**
   * Substitutes template variables in text with actual values
   *
   * Supports the following template formats:
   * - {{QuestionCode}} - Replaced with the answer value for that question
   * - {{QuestionCode|DefaultValue}} - Uses default if question has no answer
   * - {{QuestionCode|DefaultValue|Adjustment1|Adjustment2}} - Applies adjustments to the value
   * - {{Login:Name}} - Special syntax for user information
   * - {{Login:Email}} - Special syntax for user email
   * - {{Workflow:StatusCode}} - Special syntax for workflow status code
   * - {{Workflow:StatusLabel}} - Special syntax for workflow status label
   * - {{Workflow:EntityName}} - Special syntax for the primary entity's name
   * - {{Form:CreatedBy}} - Name of the user who created this form response
   * - {{Form:CreatedAt}} - Date when this form response was created
   *
   * @param {string|null|undefined} text - The text containing template variables
   * @param {string|null|undefined} repeatableGroupGuid - The repeatable group GUID for context
   * @returns {string|null|undefined} The text with all templates substituted
   */
  function substituteTemplatedText(
    text?: string | null,
    repeatableGroupGuid?: string | null,
  ): string | undefined | null {
    // Handle null and undefined to make it easier for the caller
    if (!text) {
      return text;
    }

    let substituted = text;
    if (text.includes("{{")) {
      substituted = text.replaceAll(/{{([^{}]*)}}/g, (_, match) => {
        // Parse the template parts: questionCode, defaultValue, and any adjustments
        const [questionCode, defaultValue, ...adjustments]: string[] = match
          .split("|")
          .map((s: string) => s.trim());

        let label: string | null = null;

        // Handle special template syntax with colon (e.g., Login:Name, Workflow:StatusCode)
        if (questionCode.includes(":")) {
          const [specialCode, propertyName] = questionCode.split(":");
          label = handleSpecialTemplate(specialCode, propertyName);
        }
        // Handle regular question code substitution
        else {
          label = handleRegularQuestionCode(questionCode, repeatableGroupGuid);
        }

        // If no value was found, use the default value or empty string
        if (!label) {
          return defaultValue ?? "";
        }

        // If no adjustments are specified, return the label as is
        if (!adjustments.length) {
          return label;
        }

        // Apply any specified adjustments to the label
        return processTemplateSyntaxSugar(label, adjustments);
      });
    }
    return substituted;
  }

  /**
   * Identifies all questions that require attention in the Review Answers component
   *
   * This function finds questions that either:
   * 1. Are unanswered (empty/no value)
   * 2. Have validation errors (answered but invalid)
   *
   * @returns {FormElementBase[]} Array of form elements requiring attention
   */
  function getQuestionsRequiringAttention(): FormElementBase[] {
    // Get a read-only snapshot of current answers
    const answerMap = readonly(
      answers.value,
    ) as Readonly<FormResponseAnswerMap>;

    // Rule evaluator for checking if a component is hidden
    const isHidden = getIsRuleTypeApplicableWrapper(FormElementRuleType.HIDDEN);

    // Maintain the form-defined order for final sorting
    const questionOrder = formDefinition.components.map((c) => c.guid);

    // Identify all repeater components and their GUIDs
    const repeaters = formDefinition.components.filter(
      (c) => c.type === FormElementType.REPEATER,
    );
    const repeaterGuids = new Set(repeaters.map((r) => r.guid));

    // Find all components that are children of repeaters
    const componentsInRepeaters = formDefinition.components.filter((c) =>
      repeaterGuids.has(c.parentGuid),
    );
    const componentsInRepeatersSet = new Set(
      componentsInRepeaters.map((c) => c.guid),
    );

    /**
     * Determines whether a component requires attention (unanswered OR has validation errors)
     */
    const requiresAttention = (
      component: FormElementBase,
      repeaterGuid?: string,
    ): boolean => {
      // Skip non-question elements, archived, read-only, hidden, or optional components
      if (
        nonQuestionElements.includes(component.type) ||
        component.isArchived ||
        isReadOnly(component) ||
        isHidden(component) ||
        component.properties?.isOptional
      ) {
        return false;
      }

      const key = `${component.questionCode}-${repeaterGuid ?? emptyGuid}`;
      const answerValue = answerMap[key]?.answerValue;

      // Check if unanswered
      const isUnanswered =
        component.type === FormElementType.FILE_UPLOAD
          ? !(Array.isArray(answerValue) && answerValue.length > 0)
          : answerValue === undefined ||
            answerValue === "" ||
            answerValue === null;

      if (isUnanswered) {
        return true;
      }

      // Check if answered but has validation errors
      return !isAnswerValid(component, answerValue);
    };

    const questionsRequiringAttention: FormElementBase[] = [];

    // === STEP 1: Check top-level (non-repeater) components ===
    for (const component of formDefinition.components) {
      if (
        !componentsInRepeatersSet.has(component.guid) &&
        requiresAttention(component)
      ) {
        questionsRequiringAttention.push(component);
      }
    }

    // === STEP 2: Check components inside repeaters ===
    for (const repeater of repeaters) {
      const repeaterKey = `${repeater.questionCode}-${emptyGuid}`;
      const repeaterAnswer = answerMap[repeaterKey]?.answerValue;

      // Skip if no valid answer or not a repeater answer type
      if (!repeaterAnswer || !isRepeaterAnswerValue(repeaterAnswer)) {
        continue;
      }

      const groupGuids: string[] = repeaterAnswer?.repeatableGroupGuids ?? [];

      // Find children for the current repeater
      const children = componentsInRepeaters.filter(
        (c) => c.parentGuid === repeater.guid,
      );

      // Check each instance of the repeater - flatten nested loops to reduce nesting
      const repeaterQuestions = groupGuids.flatMap((guid) =>
        children
          .filter((component) => requiresAttention(component, guid))
          .map((component) => ({ ...component, repeaterGuid: guid })),
      );
      questionsRequiringAttention.push(...repeaterQuestions);
    }

    // === STEP 3: Include unresolved nested questions (e.g. questions in compliance items) ===
    if (nestedUnansweredQuestions.value.length > 0) {
      questionsRequiringAttention.push(...nestedUnansweredQuestions.value);
    }

    // === STEP 4: Sort based on original form component order ===
    questionsRequiringAttention.sort((a, b) => {
      const aIndex = questionOrder.indexOf(a.guid);
      const bIndex = questionOrder.indexOf(b.guid);
      return aIndex - bIndex;
    });

    return questionsRequiringAttention;
  }

  /**
   * Identifies all required questions in the form that don't have valid answers
   *
   * This function performs a comprehensive analysis of the form to find all questions that:
   * 1. Are required (not optional)
   * 2. Are visible (not hidden by rules)
   * 3. Are editable (not read-only)
   * 4. Don't have a valid answer
   *
   * It handles both top-level questions and questions inside repeater groups.
   *
   * @returns {FormElementBase[]} Array of form elements that need to be answered
   */
  function getUnansweredQuestions(): FormElementBase[] {
    // Get a read-only snapshot of current answers
    const answerMap = readonly(
      answers.value,
    ) as Readonly<FormResponseAnswerMap>;

    // Rule evaluator for checking if a component is hidden
    const isHidden = getIsRuleTypeApplicableWrapper(FormElementRuleType.HIDDEN);

    // Maintain the form-defined order for final sorting
    // This helps the answer review component display questions in the correct order
    const questionOrder = formDefinition.components.map((c) => c.guid);

    // Identify all repeater components and their GUIDs
    const repeaters = formDefinition.components.filter(
      (c) => c.type === FormElementType.REPEATER,
    );
    const repeaterGuids = new Set(repeaters.map((r) => r.guid));

    // Find all components that are children of repeaters
    const componentsInRepeaters = formDefinition.components.filter((c) =>
      repeaterGuids.has(c.parentGuid),
    );
    const componentsInRepeatersSet = new Set(
      componentsInRepeaters.map((c) => c.guid),
    );

    /**
     * Determines whether a given component has a valid answer
     *
     * Different component types have different criteria for what constitutes a valid answer:
     * - File uploads: Must have at least one file
     * - Other components: Must have a non-empty value
     *
     * Note: This function only checks if the question has been answered (non-empty).
     * Use isAnswerValid() separately to check validation rules.
     *
     * @param {FormElementBase} component - The component to check
     * @param {string} repeaterGroupGuid - The repeater group GUID (if applicable)
     * @returns {boolean} True if the component has a non-empty answer
     */
    const hasValidAnswer = (
      component: FormElementBase,
      repeaterGroupGuid: string = emptyGuid,
    ): boolean => {
      const key = `${component.questionCode}-${repeaterGroupGuid}`;
      const answerValue = answerMap[key]?.answerValue;

      // Special handling for file upload components
      if (component.type === FormElementType.FILE_UPLOAD) {
        return Array.isArray(answerValue) && answerValue.length > 0;
      }

      // For all other components, check if the answer is non-empty
      return (
        answerValue !== undefined && answerValue !== "" && answerValue !== null
      );
    };

    /**
     * Determines whether a component should be counted as unanswered
     *
     * A component is considered unanswered if it:
     * 1. Is an actual question (not a static/display element)
     * 2. Is not read-only
     * 3. Is not hidden by rules
     * 4. Does not have a valid answer
     * 5. Is required (not optional)
     *
     * @param {FormElementBase} component - The component to check
     * @param {string} repeaterGuid - The repeater group GUID (if applicable)
     * @returns {boolean} True if the component is unanswered and should be filled
     */
    const isUnanswered = (
      component: FormElementBase,
      repeaterGuid?: string,
    ): boolean => {
      return (
        !nonQuestionElements.includes(component.type) && // Not a static element
        !component.isArchived && // Not archived
        !isReadOnly(component) && // Not read-only
        !isHidden(component) && // Not hidden
        !hasValidAnswer(component, repeaterGuid) && // Has no valid answer
        !component.properties?.isOptional // Not optional
      );
    };

    const unansweredQuestions: FormElementBase[] = [];

    // === STEP 1: Check top-level (non-repeater) components ===
    for (const component of formDefinition.components) {
      if (
        !componentsInRepeatersSet.has(component.guid) &&
        isUnanswered(component)
      ) {
        unansweredQuestions.push(component);
      }
    }

    // === STEP 2: Check components inside repeaters ===
    for (const repeater of repeaters) {
      const repeaterKey = `${repeater.questionCode}-${emptyGuid}`;
      const repeaterAnswer = answerMap[repeaterKey]?.answerValue;

      // Skip if no valid answer or not a repeater answer type
      if (!repeaterAnswer || !isRepeaterAnswerValue(repeaterAnswer)) {
        continue;
      }

      const groupGuids: string[] = repeaterAnswer?.repeatableGroupGuids ?? [];

      // Find children for the current repeater
      const children = componentsInRepeaters.filter(
        (c) => c.parentGuid === repeater.guid,
      );

      // Check each instance of the repeater - flatten nested loops to reduce nesting
      const repeaterQuestions = groupGuids.flatMap((guid) =>
        children
          .filter((component) => isUnanswered(component, guid))
          .map((component) => ({ ...component, repeaterGuid: guid })),
      );
      unansweredQuestions.push(...repeaterQuestions);
    }

    // === STEP 3: Include unresolved nested questions (e.g. questions in compliance items) ===
    if (nestedUnansweredQuestions.value.length > 0) {
      unansweredQuestions.push(...nestedUnansweredQuestions.value);
    }

    // === STEP 4: Sort based on original form component order ===
    unansweredQuestions.sort((a, b) => {
      const aIndex = questionOrder.indexOf(a.guid);
      const bIndex = questionOrder.indexOf(b.guid);
      return aIndex - bIndex;
    });

    return unansweredQuestions;
  }

  /**
   * Updates the list of unanswered questions from nested forms
   *
   * This function is used to track unanswered questions from nested forms
   * (like compliance items) that aren't directly part of the main form structure.
   *
   * @param {FormElementBase[]} questionsRemaining - List of unanswered questions from a nested form
   * @param {string} forFormGuid - The GUID of the nested form
   */
  function updateNestedUnansweredQuestions(
    questionsRemaining: FormElementBase[],
    forFormGuid?: string,
  ) {
    if (!forFormGuid) {
      forFormGuid = questionsRemaining[0].formDefinitionGuid;
      return;
    }
    // Remove any existing unanswered questions from the same form
    nestedUnansweredQuestions.value = nestedUnansweredQuestions.value.filter(
      (q) => q.formDefinitionGuid !== forFormGuid,
    );

    // Add the updated unanswered questions for this form
    nestedUnansweredQuestions.value.push(...questionsRemaining);
  }

  /**
   * Fetches recently updated answers from the server and updates the local answer map
   *
   * This is useful when multiple users might be editing the same form response
   * simultaneously, ensuring the local state stays in sync with server changes.
   *
   * @returns {Promise<void>}
   */
  async function refreshRecentlyUpdatedAnswers() {
    if (!formResponse?.guid) {
      showError("Form response guid is missing");
      return;
    }
    const recent = await apiClient.formResponse.getFormResponseRecentAnswers(
      myInfoStore.currentSysOrgGuid,
      formDefinition.guid,
      formResponse.guid,
    );

    updateAnswers(formDefinition, recent, answers);
  }

  // Return the public API of the composable
  return {
    /**
     * Reloads the answers with the provided answer list
     * Can also handle deleting specific answers by providing their GUIDs
     */
    reloadAnswers: (answerList, deletingGuid?, deletingRepeaterGuid?) =>
      updateAnswers(
        formDefinition,
        answerList,
        answers,
        deletingGuid,
        deletingRepeaterGuid,
      ),
    // Readonly version of answers to prevent external modification
    answers: readonly(answers.value) as Readonly<FormResponseAnswerMap>,
    // Template substitution function
    substituteTemplatedText,
    // Original form definition
    form: formDefinition,
    // Current form response being edited (if any)
    formResponse,
    // Computed pages based on page breaks
    pages,
    // Composable for working with individual answers
    useAnswer: useAnswerWrapper({
      answers,
      formResponse,
      reloadAnswers: (answerList, deletingGuid?, deletingRepeaterGuid?) =>
        updateAnswers(
          formDefinition,
          answerList,
          answers,
          deletingGuid,
          deletingRepeaterGuid,
        ),
      refreshRecentlyUpdatedAnswers,
    }),
    // Functions to check element visibility and editability
    isHidden: getIsRuleTypeApplicableWrapper(FormElementRuleType.HIDDEN),
    isReadOnly: isReadOnly,
    // Check if a question code is available in the form
    isAvailable: (questionCode: string) => {
      return formDefinition.components.some(
        (c) => c.questionCode === questionCode,
      );
    },
    // Status update functions
    updateFormResponseStatus: updateFormResponseStatus,
    updateWorkflowStatus: updateWorkflowStatus,
    updateWorkflowName: updateWorkflowName,
    // Map of in-flight answer submission requests
    inFlightRequestMap: setAnswerTimeoutMap,
    // Functions for handling unanswered questions
    getUnansweredQuestions: getUnansweredQuestions,
    getQuestionsRequiringAttention: getQuestionsRequiringAttention,
    updateNestedUnansweredQuestions: updateNestedUnansweredQuestions,
    // Function to refresh answers from server
    refreshRecentlyUpdatedAnswers,
    // Shared answers from all forms marked for sharing
    sharedAnswers,
    // The workflow associated with this form
    workflow,
  };
}

/**
 * Creates a map of answers from a list of form response answers
 *
 * The map uses a composite key format: "questionCode-repeatableGroupGuid"
 * This allows for efficient lookup of answers by question code and repeater context
 *
 * @param {FormDefinition} form - The form definition containing components
 * @param {FormResponseAnswer[]} answerList - List of answers to process
 * @returns {FormResponseAnswerMap} Map of answers keyed by question code and repeater GUID
 */
function setAnswers(
  form: FormDefinition,
  answerList: FormResponseAnswer[],
): FormResponseAnswerMap {
  return Object.fromEntries(
    answerList.map((a) => {
      // Use empty GUID if no repeatable group GUID is provided
      const repeaterGuid = a.repeatableGroupGuid ?? emptyGuid;

      // Create the composite key: "questionCode-repeatableGroupGuid"
      const key =
        form.components.find((c) => c.guid === a.formElementGuid)
          ?.questionCode +
        "-" +
        repeaterGuid;

      return [
        key,
        {
          ...a,
          // The data value is JSON stringified in the DB, so we parse here
          answerValue: JSON.parse(a.answerValue), // TODO: fix
        },
      ];
    }),
  );
}

/**
 * Updates the answers map with new answers and handles deletions
 *
 * This function:
 * 1. Removes deleted answers if specified
 * 2. Updates existing answers or adds new ones
 * 3. Parses JSON answer values from the server
 *
 * @param {FormDefinition} form - The form definition
 * @param {FormResponseAnswer[]} answerList - New answers to add/update
 * @param {Ref<FormResponseAnswerMap>} answers - Ref to the answers map to update
 * @param {string} deletingGuid - Optional GUID of an answer to delete
 * @param {string} deletingRepeaterGuid - Optional repeater GUID for the answer to delete
 */
function updateAnswers(
  form: FormDefinition,
  answerList: FormResponseAnswer[],
  answers: Ref<FormResponseAnswerMap>,
  deletingGuid?: string,
  deletingRepeaterGuid?: string,
) {
  // If we are deleting an answer, we need to remove it from the answers map
  if (deletingGuid) {
    const repeaterGuid = deletingRepeaterGuid ?? emptyGuid;

    const key =
      form.components.find((c) => c.guid === deletingGuid)?.questionCode +
      "-" +
      repeaterGuid;

    delete answers.value[key];
  }

  // IMPORTANT: Mutate answers.value in place — do NOT replace it with a new object.
  // See ./answers-reactivity.md for details.
  answerList.forEach((a) => {
    const repeaterGuid = a.repeatableGroupGuid ?? emptyGuid;

    // Create the composite key for this answer
    const key =
      form.components.find((c) => c.guid === a.formElementGuid)?.questionCode +
      "-" +
      repeaterGuid;

    if (!key) {
      showError(
        `Question code not found for answer with guid ${a.formElementGuid}`,
      );
      return;
    }

    // Update or add the answer in the map
    answers.value[key] = {
      ...a,
      // Parse the JSON answer value from the server
      answerValue: JSON.parse(a.answerValue),
    };
  });
}
